import { useEffect, useMemo, useRef, useState } from "react";

import { runsCapFor as domainRunsCapFor } from "../../shared/domain/gameReducer";
import type { BaseballStats } from "../../shared/domain/gameReducer";
import type { ScheduleField } from "../../shared/domain/schedule";
import {
  clearPlayerClaim,
  friendlyError,
  mergeMemberIdentity,
  newActionId,
} from "../data/callables";
import { timeAgo } from "../data/viewModel";
import {
  buildCurrentStatRows,
  buildSeasonSummary,
} from "../domain/baseballStats";
import {
  buildInviteUrl,
  createJerseyMoment,
  isOpenInning as calculateIsOpenInning,
  selectBatters,
  selectBattingList,
  summarizeScore,
} from "../domain/gamePresentation";
import {
  buildStatsWorkbookFilename,
  buildStatsWorkbookRows,
} from "../domain/statsWorkbook";
import { playoffGameAvailability } from "../domain/playoffSeries";
import {
  loadWorkbookLibrary,
  writeStatsWorkbook,
} from "../data/statsWorkbookAdapter";
import { normalizeApplicationError } from "../shared/errors";
import type { ArchivedGameStats } from "../domain/baseballStats";
import type { SeasonModel } from "../shared/types/contracts";
import type {
  PresentedScheduleEntry,
  TeamAppModel,
  TeamController,
  TeamMember,
  TeamUser,
} from "./teamTypes";
import { actionKeys } from "./actionKeys";

const EMPTY_STATS: Record<string, BaseballStats> = {};
interface SeasonRecord {
  seasonId?: string | null;
  scheduledDate?: string | null;
  date?: string | null;
}

const seasonIdFor = (record: SeasonRecord): string => {
  if (record.seasonId) return record.seasonId;
  const date = record.scheduledDate ?? record.date ?? "";
  return /^\d{4}-/.test(date) ? date.slice(0, 4) : "legacy";
};

interface TeamAppPresenterOptions {
  teamId: string;
  user: TeamUser | null | undefined;
  member: TeamMember | null | undefined;
  linkGoogle?: (() => Promise<TeamUser>) | null;
  controller: TeamController;
}

export function useTeamAppPresenter({
  teamId,
  user,
  member,
  linkGoogle,
  controller,
}: TeamAppPresenterOptions): TeamAppModel {
  const {
    data: {
      teamName,
      activeSeasonId,
      players,
      prefs,
      activePlayers,
      jerseyHolderPlayerId,
      pregame,
      game,
      authoritativeGameRevision,
      log,
      gameArchive,
      scheduleGames,
      playoffSeries,
      seasons,
      activity,
      presence,
      invite,
      activityLoading,
    },
    status: {
      canScore,
      undoCrossesHalfInning,
      halfInningTransition,
      completedHalfInningRuns,
      isScorer,
      scorerName,
      scoringClaimed,
      serverBusy,
      connectionState,
      intentStatus,
      trackIntent,
    },
    ui,
    actions,
  } = controller;
  const [newName, setNewName] = useState("");
  const [inviteCopied, setInviteCopied] = useState(false);
  const [linkedEmail, setLinkedEmail] = useState<string | null>(null);
  const [linkBusy, setLinkBusy] = useState(false);
  const [confirmUnclaim, setConfirmUnclaim] = useState(false);
  const [unclaimBusy, setUnclaimBusy] = useState(false);
  const [statsSeasonId, setStatsSeasonId] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const previousSeasonContext = useRef({ teamId, activeSeasonId });
  const inviteUrl = buildInviteUrl(
    window.location.origin,
    teamId,
    invite.token,
  );

  const activeIds = game
    ? game.lineup
        .map((entry) => entry.id)
        .filter((id): id is string => Boolean(id))
    : pregame.activeIds;
  const lineupSlots = game
    ? game.lineup.map((entry) => entry.id)
    : pregame.lineupSlots;
  const lineupMode = game
    ? game.status === "in_progress"
      ? "live"
      : "locked"
    : "pregame";
  const gamePitcher = pregame.gamePitcher;
  const defense = game ? game.defense : pregame.defense;
  const opponentName = game ? game.oppName : pregame.oppName;
  const isHome = game ? game.isHome : pregame.isHome;
  const openInning = game ? game.openInning : pregame.openInning;
  const gameFinal = game?.gameFinal ?? false;
  const mercyRuleWinner = game?.mercyRuleWinner ?? null;
  const mercyRuleSide = game?.mercyRuleSide ?? null;
  const mercyRuleRunsRemaining = game?.mercyRuleRunsRemaining ?? null;
  const mercyRuleRunsNeededToPrevent =
    game?.mercyRuleRunsNeededToPrevent ?? null;
  const inning = game?.inning ?? 1;
  const batting = game?.batting ?? true;
  const outs = game?.outs ?? 0;
  const batterIndex = game?.batterIdx ?? 0;
  const bases = game?.bases ?? [null, null, null];
  const ourRunsByInning = game?.ourRunsByInning ?? [0];
  const opponentRunsByInning = game?.oppRunsByInning ?? [0];
  const stats = game?.stats ?? EMPTY_STATS;
  const historyLength = game?.undoStack?.length ?? 0;
  const {
    ourScore,
    oppScore: opponentScore,
    finalLabel,
  } = summarizeScore(
    ourRunsByInning,
    opponentRunsByInning,
    game?.forfeitWinner,
  );
  const mercyRulePending =
    mercyRuleSide !== null && mercyRuleRunsRemaining !== null;
  const battingList = selectBattingList(
    game?.lineup ?? null,
    lineupSlots,
    players,
  );
  const { batter, nextBatters } = selectBatters(battingList, batterIndex);
  const isOpenInning = calculateIsOpenInning(inning, openInning);
  const isJerseyBatter = !!game && batter?.id === jerseyHolderPlayerId;
  const jerseyMoment = useMemo(
    () => createJerseyMoment(batter?.id, Math.random()),
    [batter?.id],
  );
  const statRows = useMemo(
    () => buildCurrentStatRows(players, stats),
    [players, stats],
  );
  const availableSeasons = useMemo<SeasonModel[]>(() => {
    const byId = new Map<string, SeasonModel>(
      (seasons ?? []).map((season) => [season.id, season]),
    );
    if (activeSeasonId && !byId.has(activeSeasonId)) {
      const year = /^\d{4}$/.test(activeSeasonId)
        ? Number(activeSeasonId)
        : null;
      byId.set(activeSeasonId, {
        id: activeSeasonId,
        year,
        label: year ? String(year) : activeSeasonId,
        status: "active",
        revision: 0,
      });
    }
    for (const record of [...gameArchive, ...(scheduleGames ?? [])]) {
      const id = seasonIdFor(record);
      if (!byId.has(id)) {
        const year = /^\d{4}$/.test(id) ? Number(id) : null;
        byId.set(id, {
          id,
          year,
          label: year ? String(year) : "Legacy",
          status: id === activeSeasonId ? "active" : "closed",
          revision: 0,
        });
      }
    }
    return [...byId.values()].sort(
      (left, right) =>
        (right.year ?? Number.NEGATIVE_INFINITY) -
        (left.year ?? Number.NEGATIVE_INFINITY),
    );
  }, [activeSeasonId, gameArchive, scheduleGames, seasons]);
  useEffect(() => {
    const previous = previousSeasonContext.current;
    previousSeasonContext.current = { teamId, activeSeasonId };
    if (
      previous.teamId !== teamId ||
      (activeSeasonId && previous.activeSeasonId !== activeSeasonId)
    ) {
      setStatsSeasonId(activeSeasonId ?? availableSeasons[0]?.id ?? "all");
      return;
    }
    if (
      statsSeasonId &&
      (statsSeasonId === "all" ||
        availableSeasons.some((season) => season.id === statsSeasonId))
    ) {
      return;
    }
    setStatsSeasonId(activeSeasonId ?? availableSeasons[0]?.id ?? "all");
  }, [activeSeasonId, availableSeasons, statsSeasonId, teamId]);
  const selectedSeasonId = statsSeasonId ?? activeSeasonId ?? "all";
  const selectedSeasonGames = useMemo(
    () =>
      selectedSeasonId === "all"
        ? gameArchive
        : gameArchive.filter(
            (archivedGame) => seasonIdFor(archivedGame) === selectedSeasonId,
          ),
    [gameArchive, selectedSeasonId],
  );
  const regularSeasonGames = useMemo(
    () =>
      selectedSeasonGames.filter(
        (archivedGame) => !archivedGame.playoffSeriesId,
      ),
    [selectedSeasonGames],
  );
  const playoffGames = useMemo(
    () =>
      selectedSeasonGames.filter(
        (archivedGame) => archivedGame.playoffSeriesId,
      ),
    [selectedSeasonGames],
  );
  const regularSeasonSummary = useMemo(
    () => buildSeasonSummary(regularSeasonGames, players),
    [players, regularSeasonGames],
  );
  const playoffSummary = useMemo(
    () => buildSeasonSummary(playoffGames, players),
    [players, playoffGames],
  );
  const scheduleEntries = useMemo<PresentedScheduleEntry[]>(() => {
    const archivedById = new Map<string, ArchivedGameStats>(
      gameArchive
        .filter((gameEntry): gameEntry is ArchivedGameStats & { id: string } =>
          Boolean(gameEntry.id),
        )
        .map((gameEntry) => [gameEntry.id, gameEntry]),
    );
    const addResult = (
      entry: PresentedScheduleEntry,
    ): PresentedScheduleEntry => {
      const archivedGame = entry.gameId ? archivedById.get(entry.gameId) : null;
      if (!archivedGame) return entry;
      return {
        ...entry,
        result: {
          ourScore: archivedGame.ourScore,
          oppScore: archivedGame.oppScore,
          forfeitWinner: archivedGame.forfeitWinner ?? null,
        },
      };
    };
    const linkedIds = new Set<string>(
      (scheduleGames ?? [])
        .map((entry) => entry.gameId)
        .filter((id): id is string => Boolean(id)),
    );
    const legacyResults: PresentedScheduleEntry[] = gameArchive
      .filter((gameEntry) => !gameEntry.id || !linkedIds.has(gameEntry.id))
      .map((gameEntry): PresentedScheduleEntry => ({
        id: `legacy-${gameEntry.id ?? "unknown"}`,
        seasonId: seasonIdFor(gameEntry),
        status: "completed",
        gameId: gameEntry.id ?? null,
        revision: 0,
        scheduledDate: gameEntry.scheduledDate ?? gameEntry.date,
        timeSlot: gameEntry.timeSlot ?? null,
        field: (gameEntry.field as ScheduleField | null | undefined) ?? null,
        opponentName: gameEntry.opponent,
        isHome: gameEntry.isHome ?? false,
        beerDuty: gameEntry.beerDuty ?? null,
        playoffSeriesId: gameEntry.playoffSeriesId ?? null,
        seriesGameNumber: gameEntry.seriesGameNumber ?? null,
      }))
      .map(addResult);
    const seriesById = new Map(
      (playoffSeries ?? []).map((series) => [series.id, series]),
    );
    return [...(scheduleGames ?? []).map(addResult), ...legacyResults].map(
      (entry): PresentedScheduleEntry => {
        const series = entry.playoffSeriesId
          ? seriesById.get(entry.playoffSeriesId)
          : null;
        return {
          ...entry,
          seriesAvailability:
            series && entry.seriesGameNumber
              ? playoffGameAvailability(series, entry.seriesGameNumber)
              : null,
        };
      },
    );
  }, [gameArchive, scheduleGames, playoffSeries]);
  const resultGame =
    gameArchive.find((gameEntry) => gameEntry.id === ui.selectedResultGameId) ??
    null;
  const googleEmail =
    linkedEmail ??
    user?.providerData?.find(
      (provider) => provider?.providerId === "google.com",
    )?.email ??
    null;
  const activityItems = activity.map((entry) => ({
    ...entry,
    occurredLabel: timeAgo(entry.occurredAt),
  }));
  const errorMessage = (error: unknown): string =>
    normalizeApplicationError(error, friendlyError(error)).message;
  const errorCode = (error: unknown): string | null =>
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    typeof error.code === "string"
      ? error.code
      : null;

  const addPlayer = async (): Promise<void> => {
    const name = newName.trim();
    if (!name) return;
    if (await actions.addPlayer(name)) setNewName("");
  };
  const onRotateInvite = async (): Promise<void> => {
    const succeeded = await actions.rotateInviteLink();
    if (succeeded) {
      setInviteCopied(false);
      ui.setConfirmAction(null);
      ui.showToast("New invite link ready — old links are dead");
    }
  };
  const copyInviteLink = async (): Promise<void> => {
    if (!inviteUrl) return;
    let copied = false;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      copied = true;
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = inviteUrl;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      try {
        copied = document.execCommand("copy");
      } catch {
        copied = false;
      }
      document.body.removeChild(textarea);
    }
    if (copied) {
      setInviteCopied(true);
      setTimeout(() => setInviteCopied(false), 2000);
    } else {
      ui.showToast("Couldn't copy — tap the link and copy it manually");
    }
  };
  const onRevokeInvite = async (): Promise<void> => {
    if (await actions.revokeInviteLink()) {
      ui.setConfirmAction(null);
      ui.showToast("Invite revoked — rotate a new link to invite again");
    }
  };
  const unclaimPlayer = async (): Promise<void> => {
    if (unclaimBusy || !member?.claimedPlayerId) return;
    setUnclaimBusy(true);
    try {
      await trackIntent(actionKeys.unclaimPlayer, () =>
        clearPlayerClaim({
          teamId,
          actionId: newActionId(),
          playerId: member.claimedPlayerId as string,
        }),
      );
      ui.showToast("Player unclaimed — pick your player again");
      setConfirmUnclaim(false);
      ui.setManageOpen(false);
    } catch (error) {
      ui.showToast(errorMessage(error));
    } finally {
      setUnclaimBusy(false);
    }
  };
  const linkAccount = async (): Promise<void> => {
    if (!linkGoogle || linkBusy) return;
    setLinkBusy(true);
    try {
      const linked = await trackIntent(actionKeys.linkAccount, async () => {
        const linkedUser = await linkGoogle();
        await mergeMemberIdentity({ teamId });
        return linkedUser;
      });
      const email = linked?.providerData?.find(
        (provider) => provider?.providerId === "google.com",
      )?.email;
      setLinkedEmail(email || "linked");
      ui.showToast("Google account linked");
    } catch (error) {
      const code = errorCode(error);
      if (
        code !== "auth/popup-closed-by-user" &&
        code !== "auth/cancelled-popup-request"
      ) {
        ui.showToast(
          code === "auth/credential-already-in-use"
            ? "That Google account is already linked to someone else."
            : errorMessage(error),
        );
      }
    } finally {
      setLinkBusy(false);
    }
  };
  const discardGame = async (): Promise<void> => {
    if (await actions.discardActiveGame()) {
      ui.setConfirmAction(null);
      ui.setManageOpen(false);
    }
  };
  const exportExcel = async (): Promise<void> => {
    setExportError(null);
    try {
      await trackIntent(actionKeys.exportStats, async () => {
        const workbookLibrary = await loadWorkbookLibrary();
        const workbookRows = buildStatsWorkbookRows({
          battingList,
          statsByPlayerId: stats,
          gameArchive,
          activePlayers,
          playoffSeries,
          currentGamePlayoffSeriesId: game?.playoffSeriesId ?? null,
        });
        const filename = buildStatsWorkbookFilename(
          teamName,
          new Date().toISOString().slice(0, 10),
        );
        writeStatsWorkbook(workbookLibrary, workbookRows, filename);
      });
    } catch {
      const message =
        "Export failed to load — check your connection and try again";
      setExportError(message);
      ui.showToast(message);
    }
  };
  const runsCapFor = (forUs: boolean): number =>
    domainRunsCapFor(
      {
        openInning,
        state: {
          inning,
          ourRunsByInning,
          opponentRunsByInning,
        },
      },
      forUs,
    );

  const selectedScheduleEntry = scheduleEntries.find(
    (entry) => entry.id === (game?.scheduleId ?? pregame.selectedScheduleId),
  );
  const setupOpponentName = game
    ? game.oppName
    : (selectedScheduleEntry?.opponentName ?? pregame.oppName);
  const setupIsHome = game
    ? game.isHome
    : (selectedScheduleEntry?.isHome ?? pregame.isHome);
  const setupScheduledDate = game
    ? game.scheduledDate
    : (selectedScheduleEntry?.scheduledDate ?? pregame.scheduledDate);
  const setupTimeSlot = game
    ? game.timeSlot
    : (selectedScheduleEntry?.timeSlot ?? pregame.timeSlot);
  const setupField = game
    ? game.field
    : (selectedScheduleEntry?.field ?? pregame.field);
  const setupBeerDuty = game
    ? game.beerDuty
    : (selectedScheduleEntry?.beerDuty ?? pregame.beerDuty);
  const setupAuthoritativeRevision = game
    ? (authoritativeGameRevision ?? game.revision)
    : (selectedScheduleEntry?.revision ?? pregame.revision);
  const setupWeatherSource = {
    scheduledDate: setupScheduledDate,
    timeSlot: setupTimeSlot,
    field: setupField,
  };
  const setupWeatherBaseId =
    game?.scheduleId ?? pregame.selectedScheduleId ?? "current-setup";
  const setupWeatherEntryExists = scheduleEntries.some(
    (entry) => entry.id === setupWeatherBaseId,
  );
  const canBuildSetupWeather = Boolean(
    setupWeatherSource.scheduledDate &&
    setupWeatherSource.timeSlot &&
    setupWeatherSource.field,
  );
  const setupWeatherId =
    setupWeatherEntryExists || canBuildSetupWeather ? setupWeatherBaseId : null;
  const weatherEntries =
    ui.screen === "setup" &&
    setupWeatherId &&
    !setupWeatherEntryExists &&
    canBuildSetupWeather
      ? [
          ...scheduleEntries,
          {
            id: setupWeatherId,
            status: game ? ("active" as const) : ("scheduled" as const),
            scheduledDate: setupWeatherSource.scheduledDate ?? "",
            timeSlot: setupWeatherSource.timeSlot,
            field: setupWeatherSource.field,
          },
        ]
      : scheduleEntries;

  return {
    shell: {
      screen: ui.screen,
      setScreen: ui.setScreen,
      teamName,
      activeSeasonId,
      hasActiveGame: !!game,
      opponentName,
      inning,
      batting,
      ourScore,
      opponentScore,
      connectionState,
      isScorer,
      scorerName,
      scoringClaimed,
      manageOpen: ui.manageOpen,
      setManageOpen: ui.setManageOpen,
      toast: ui.toast,
      claimScoring: actions.claimScoring,
      actionStatus: intentStatus,
      retryUnknown: intentStatus.retryUnknown,
    },
    management: {
      googleEmail,
      linkBusy,
      claimedPlayerId: member?.claimedPlayerId ?? null,
      hasActiveGame: !!game,
      seasons: availableSeasons,
      activeSeasonId,
      serverBusy,
      actionStatus: intentStatus,
      players,
      confirmUnclaim,
      unclaimBusy,
      teamName,
      teamNameRevision: pregame.revision,
      newPlayerName: newName,
      confirmation: ui.confirmAction,
      invite,
      inviteUrl,
      inviteCopied,
      presence,
      currentUserId: user?.uid ?? null,
      activityLoading,
      activity: activityItems,
      linkAccount,
      setConfirmUnclaim,
      unclaimPlayer,
      setTeamName: actions.setTeamName,
      setNewPlayerName: setNewName,
      addPlayer,
      setConfirmation: ui.setConfirmAction,
      discardGame,
      startSeason: actions.startSeason,
      closeSeason: actions.closeSeason,
      reopenSeason: actions.reopenSeason,
      copyInviteLink,
      rotateInvite: onRotateInvite,
      revokeInvite: onRevokeInvite,
    },
    setup: {
      game,
      pregame,
      players,
      activeIds,
      lineupSlots,
      lineupMode,
      preferences: prefs,
      expandedPlayerId: ui.expandedPlayer,
      setExpandedPlayerId: ui.setExpandedPlayer,
      confirmDeletePlayerId: ui.confirmDelete,
      setConfirmDeletePlayerId: ui.setConfirmDelete,
      jerseyHolderPlayerId,
      opponentName: setupOpponentName,
      opponentLocked: Boolean(selectedScheduleEntry?.playoffSeriesId),
      isHome: setupIsHome,
      authoritativeRevision: setupAuthoritativeRevision,
      scheduledDate: setupScheduledDate,
      timeSlot: setupTimeSlot,
      field: setupField,
      beerDuty: setupBeerDuty,
      battingCount: battingList.length,
      serverBusy,
      actionStatus: intentStatus,
      actions: {
        setAttendance: actions.setAttendance,
        togglePref: actions.togglePref,
        setJerseyHolder: actions.setJerseyHolder,
        removePlayer: actions.removePlayer,
        suggestLineup: actions.suggestLineup,
        reorderLineup: actions.reorderLineup,
        setOpponentName: actions.setOpponentName,
        setHomeTeam: actions.setHomeTeam,
        setScheduleDetails: actions.setScheduleDetails,
        startGame: actions.startGame,
      },
    },
    schedule: {
      teamName,
      entries: scheduleEntries,
      series: playoffSeries,
      seasons: availableSeasons,
      activeSeasonId,
      players,
      serverBusy,
      actionStatus: intentStatus,
      setResultGameId: ui.setSelectedResultGameId,
      actions: {
        createSchedule: actions.createSchedule,
        updateSchedule: actions.updateSchedule,
        selectSchedule: actions.selectSchedule,
        deleteSchedule: actions.deleteSchedule,
        createSeries: actions.createSeries,
        updateSeries: actions.updateSeries,
        deleteSeries: actions.deleteSeries,
      },
    },
    result: { game: resultGame },
    stats: {
      gameId: game?.id ?? null,
      rows: statRows,
      archivedGameCount: gameArchive.length,
      serverBusy,
      actionStatus: intentStatus,
      exportError,
      archive: actions.endAndArchive,
      exportExcel,
    },
    season: {
      regularSummary: regularSeasonSummary,
      playoffSummary,
      regularGames: regularSeasonGames,
      playoffGames,
      seasons: availableSeasons,
      activeSeasonId,
      hasActiveGame: !!game,
      selectedSeasonId,
      setSelectedSeasonId: setStatsSeasonId,
    },
    defense: {
      teamName,
      players,
      activePlayers,
      preferences: prefs,
      defense,
      selectedInning: ui.defInning,
      setSelectedInning: ui.setDefInning,
      gamePitcher,
      hasActiveGame: !!game,
      exportView: ui.exportView,
      setExportView: ui.setExportView,
      fieldInning: ui.fieldInning,
      actionStatus: intentStatus,
      setFieldInning: ui.setFieldInning,
      actions: {
        setPitcher: actions.setPitcher,
        regenerate: actions.regenerate,
        setManualAssign: actions.setManualAssign,
      },
    },
    game: {
      gameId: game?.id ?? null,
      teamName,
      opponentName,
      isHome,
      inning,
      batting,
      halfInningTransition,
      completedHalfInningRuns,
      outs,
      ourScore,
      opponentScore,
      ourRunsByInning,
      opponentRunsByInning,
      gameFinal,
      mercyRulePending,
      mercyRuleWinner,
      mercyRuleSide,
      mercyRuleRunsRemaining,
      mercyRuleRunsNeededToPrevent,
      finalLabel,
      serverBusy,
      actionStatus: intentStatus,
      canScore,
      isOpenInning,
      openInning,
      currentRunCap: runsCapFor(batting),
      opponentRunCap: runsCapFor(false),
      pendingRbi: ui.pendingRBI,
      pendingPlay: ui.pendingPlay,
      players,
      battingList,
      bases,
      batterIndex,
      batter,
      nextBatters,
      isJerseyBatter,
      jerseyCaption: jerseyMoment.caption,
      showLegend: ui.showLegend,
      setShowLegend: ui.setShowLegend,
      log,
      logOpen: ui.logOpen,
      setLogOpen: ui.setLogOpen,
      preferences: prefs,
      defense,
      historyLength,
      undoCrossesHalfInning,
      actions: {
        advanceRunner: actions.advanceRunner,
        removeRunner: actions.removeRunner,
        applyRBI: actions.applyRBI,
        setDecision: actions.setDecision,
        adjustPendingRbi: actions.adjustPendingRbi,
        submitPlay: actions.submitPlay,
        cancelPlay: actions.cancelPlay,
        selectResult: actions.selectResult,
        endHalf: actions.endHalf,
        endAndArchive: actions.endAndArchive,
        forfeitGame: actions.forfeitGame,
        keepPlaying: actions.keepPlaying,
        toggleOpenInning: actions.toggleOpenInning,
        addOppRun: actions.addOppRun,
        confirmHalfInningTransition: actions.confirmHalfInningTransition,
        undo: actions.undo,
      },
    },
    weatherEntries,
    setupWeatherId,
  };
}
